import type { LinearTransform, Point } from "./orientedResize.helpers"
import { toLocalDelta } from "./orientedResize.helpers"

/**
 * Where an element's own "left / top" coordinates land on the screen — measured with a few
 * invisible probes, so every ancestor's rotation, scale, skew, border and animation is in it.
 * Summing up the parents' top / left knows about none of that.
 */

/** How a containing block's own coordinates land on the screen: origin + linear part. */
export type SpaceT = { origin: Point; axes: LinearTransform }

/** Long enough for the probes' distance to be measured precisely. */
const PROBE_DISTANCE = 100

const pointOf = (element: HTMLElement): Point => {
    const rect = element.getBoundingClientRect()
    return { x: rect.left, y: rect.top }
}

/** Measure the space of a positioned element (the containing block of what sits inside it). */
export const measureSpace = (containingBlock: HTMLElement): SpaceT => {
    const probe = (left: number, top: number) => {
        const element = document.createElement("div")
        Object.assign(element.style, {
            position: "absolute",
            left: `${left}px`,
            top: `${top}px`,
            width: "0",
            height: "0",
            visibility: "hidden",
            pointerEvents: "none",
        } as Partial<CSSStyleDeclaration>)
        containingBlock.appendChild(element)
        const point = pointOf(element)
        element.remove()
        return point
    }
    const origin = probe(0, 0)
    const alongX = probe(PROBE_DISTANCE, 0)
    const alongY = probe(0, PROBE_DISTANCE)
    return {
        origin,
        axes: {
            a: (alongX.x - origin.x) / PROBE_DISTANCE,
            b: (alongX.y - origin.y) / PROBE_DISTANCE,
            c: (alongY.x - origin.x) / PROBE_DISTANCE,
            d: (alongY.y - origin.y) / PROBE_DISTANCE,
        },
    }
}

/**
 * The space a new child of `parentElement` (the parent object's element, or the canvas for the
 * root) is positioned in. Every canvas object sits in a slot of plain blocks ending with a
 * `position: relative` one (see CanvasItem / AbstractContainer); an empty slot, appended
 * after the parent's children, gives the same space.
 */
export const measureChildSpace = (parentElement: HTMLElement): SpaceT => {
    const slot = document.createElement("div")
    const containingBlock = document.createElement("div")
    containingBlock.style.position = "relative"
    slot.appendChild(containingBlock)
    parentElement.appendChild(slot)
    const space = measureSpace(containingBlock)
    slot.remove()
    return space
}

/**
 * Whether a space squashes everything onto a point or a line (a `scale(0)` parent, an element
 * that renders no children, such as an <img>): nothing can be placed in it by measuring.
 */
export const isDegenerate = (space: SpaceT): boolean =>
    Math.abs(space.axes.a * space.axes.d - space.axes.b * space.axes.c) < 1e-6

/** Where a point lies in a space. */
export const toSpace = (space: SpaceT, point: Point): Point =>
    toLocalDelta(space.axes, { x: point.x - space.origin.x, y: point.y - space.origin.y })

/** Where a point of a space lies outside it. */
export const fromSpace = (space: SpaceT, point: Point): Point => ({
    x: space.origin.x + space.axes.a * point.x + space.axes.c * point.y,
    y: space.origin.y + space.axes.b * point.x + space.axes.d * point.y,
})

/**
 * A screen space in absolute canvas units, the units the selection chrome is drawn in (see
 * measureInCanvas): from the canvas' top-left corner, its zoom taken out.
 */
const toCanvasUnits = (space: SpaceT, canvasElement: HTMLElement): SpaceT => {
    const rect = canvasElement.getBoundingClientRect()
    const scale = canvasElement.offsetWidth > 0 ? rect.width / canvasElement.offsetWidth : 1
    return {
        origin: {
            x: (space.origin.x - rect.left) / scale,
            y: (space.origin.y - rect.top) / scale,
        },
        axes: {
            a: space.axes.a / scale,
            b: space.axes.b / scale,
            c: space.axes.c / scale,
            d: space.axes.d / scale,
        },
    }
}

/**
 * The space — in absolute canvas units — of the parent of every object among `ids` whose parent
 * is not among them, i.e. that a multiselection resize places in a parent staying as it is.
 * Root objects are left out: the canvas is their space, the styles already tell the truth.
 */
export const measureParentSpaces = (
    ids: string[],
    getParentId: (id: string) => string | null | undefined,
    getElement: (id: string) => HTMLElement | null | undefined,
    canvasElement: HTMLElement
): Record<string, SpaceT> => {
    const included = new Set(ids)
    const spaces: Record<string, SpaceT> = {}
    ids.forEach((id) => {
        const parentId = getParentId(id)
        if (!parentId || included.has(parentId)) return
        const containingBlock = getElement(id)?.parentElement
        if (!containingBlock) return
        spaces[id] = toCanvasUnits(measureSpace(containingBlock), canvasElement)
    })
    return spaces
}
